import React from "react";
import PropTypes from "prop-types";
import { CheckCircle } from "@phosphor-icons/react";
import {
  VerdictBarContainer,
  VerdictBarTitle,
} from "../AIPane/AIPaneStyled";
import { WorkItemCard } from "../WorkItems/MatterWorkPanel";

/**
 * The verdict bar — the return half of the conveyor belt.
 *
 * The WaitingOnBanner (amber, above) says what the matter is still
 * waiting FOR; this bar (green) says what the open document already
 * ANSWERS. When the requester opens the returned evidence, the work
 * item it came back on is right here with their verbs in hand —
 * "Accept & close" or "Send back" with a note — so the review happens
 * where the evidence is on screen, not three clicks away. Renders
 * nothing when the document belongs to no open loop.
 *
 * Rows are full WorkItemCards (instruction, thread, doorway-granted
 * verbs), expanded when there's exactly one loop — the common case
 * should arrive ready to act on.
 */
const DocVerdictBar = ({
  items = [],
  onActed,
  title = "This document answers",
  showMatter = true,
}) => {
  if (items.length === 0) return null;

  return (
    <VerdictBarContainer
      role="note"
      aria-label={title}
      data-testid="doc-verdict-bar"
    >
      <VerdictBarTitle>
        <CheckCircle size={13} weight="bold" />
        {title}
      </VerdictBarTitle>
      {items.map((item) => (
        <WorkItemCard
          key={item.id}
          item={item}
          onActed={onActed}
          matterLabel={(showMatter && item.matterName) || undefined}
          defaultExpanded={items.length === 1}
          closeLabel="Accept & close"
        />
      ))}
    </VerdictBarContainer>
  );
};

DocVerdictBar.propTypes = {
  /** Rows from the waiting-on endpoint's `linked` list (doorway
   * `view=for-document` shape — verbs included). */
  items: PropTypes.arrayOf(PropTypes.object),
  onActed: PropTypes.func.isRequired,
  title: PropTypes.string,
  /** Off inside a matter's own document view, where the matter is
   * already on screen. */
  showMatter: PropTypes.bool,
};

export default DocVerdictBar;
